import { ChevronRight, X, type LucideIcon } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
} from '@/components/ui/actionsheet';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';

/**
 * One thing the composer can attach. The sheet closes before `onSelect` runs.
 *
 * `tile` is for the few sources picked most often, drawn side by side across
 * the top. `row` is for everything else, full width beneath them, and suits an
 * option that opens a second step, which `opensMore` marks with a chevron.
 */
export type AttachMenuOption = {
  key: string;
  label: string;
  /** What a screen reader hears after the label, since the tile shows only a word. */
  hint: string;
  icon: LucideIcon;
  placement: 'tile' | 'row';
  opensMore?: boolean;
  onSelect: () => void;
};

type SupportAttachMenuSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  options: AttachMenuOption[];
};

/** The close button's visual size; at 48px it is already the minimum target. */
const CLOSE_BUTTON_CLASS = 'h-12 w-12';

/**
 * What the composer's attach button opens, laid out the way modern chat apps
 * do: a row of large tiles for the everyday sources (camera, photos, files)
 * and full-width rows below for the rest. The options come in as data, so a
 * new kind of attachment is one more entry at the call site, and the caller
 * decides which apply to the viewer (only staff attach an inquiry).
 *
 * The sheet draws in the root overlay rather than a native modal, so an
 * option can open a system picker straight after closing it.
 */
export function SupportAttachMenuSheet({ isOpen, onClose, options }: SupportAttachMenuSheetProps) {
  const { bottom } = useSafeAreaInsets();

  const tiles = options.filter((option) => option.placement === 'tile');

  const rows = options.filter((option) => option.placement === 'row');

  const select = (option: AttachMenuOption) => {
    onClose();

    option.onSelect();
  };

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-[32px] bg-background-50 p-0"
        style={{ paddingBottom: bottom + 16 }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <HStack className="w-full items-center px-4 pb-4 pt-1">
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
            className={twJoin(
              CLOSE_BUTTON_CLASS,
              'items-center justify-center rounded-full bg-background-0 shadow-soft-1 web:cursor-pointer data-[hover=true]:bg-background-100'
            )}>
            <Icon as={X} className="h-5 w-5 text-typography-900" />
          </Pressable>

          <Heading size="md" className="flex-1 text-center text-typography-900">
            Add to message
          </Heading>

          <View className={CLOSE_BUTTON_CLASS} aria-hidden />
        </HStack>

        <VStack className="w-full gap-3 px-4">
          {tiles.length > 0 ? (
            <HStack className="gap-3">
              {tiles.map((option) => (
                <Pressable
                  key={option.key}
                  onPress={() => select(option)}
                  accessibilityRole="button"
                  accessibilityLabel={option.label}
                  accessibilityHint={option.hint}
                  className="h-28 flex-1 items-center justify-center gap-2 rounded-3xl bg-background-100 web:cursor-pointer data-[hover=true]:bg-background-200 data-[active=true]:bg-background-200">
                  <Icon as={option.icon} className="h-6 w-6 text-typography-900" />
                  <Text className="text-base text-typography-900">{option.label}</Text>
                </Pressable>
              ))}
            </HStack>
          ) : null}

          {rows.map((option) => (
            <Pressable
              key={option.key}
              onPress={() => select(option)}
              accessibilityRole="button"
              accessibilityLabel={option.label}
              accessibilityHint={option.hint}
              className="min-h-14 flex-row items-center gap-3 rounded-3xl bg-background-0 px-5 py-4 web:cursor-pointer data-[hover=true]:bg-background-100 data-[active=true]:bg-background-100">
              <Icon as={option.icon} className="h-5 w-5 text-typography-900" />
              <Text className="flex-1 text-base text-typography-900">{option.label}</Text>
              {option.opensMore ? (
                <Icon as={ChevronRight} className="h-5 w-5 text-typography-700" />
              ) : null}
            </Pressable>
          ))}
        </VStack>
      </ActionsheetContent>
    </Actionsheet>
  );
}
